// unmanaged
// Shared pieces of the Projects pages: labels, chips, the 8-step stage bar,
// and the dashboard's filters. The milestone list itself comes from the
// server (project-request meta), never from here.
import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";

export const STATUS_LABEL = {
  active: "Active",
  on_hold: "On hold",
  waiting_on_customer: "Waiting on customer",
  closed: "Closed",
  cancelled: "Cancelled",
};

export const HEALTH = {
  green: { label: "On track", color: "#16A34A" },
  yellow: { label: "Needs attention", color: "#D97706" },
  red: { label: "Customer concern", color: "#DC2626" },
};

export const PRIORITY_LABEL = { high: "High", medium: "Medium", low: "Low" };

export const ATTENTION_LABEL = {
  no_lead: "No project lead",
  health: "Health",
  no_next_action: "No next action",
  overdue: "Overdue",
};

export const ROLE_LABEL = {
  project_lead: "Project lead",
  analysis_lead: "Analysis lead",
  data_scientist: "Data scientist",
  scientific_reviewer: "Scientific reviewer",
  presentation_owner: "Presentation owner",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-10-15" -> "Oct 15" (a calendar date: no time-zone shift).
export function formatDay(isoDate) {
  if (!isoDate) return "";
  const [, m, d] = String(isoDate).split("-").map(Number);
  return m && d ? `${MONTHS[m - 1]} ${d}` : String(isoDate);
}

// Whole days from `due` to `today`, both YYYY-MM-DD; positive when late.
export function daysLate(due, today) {
  if (!due || !today) return 0;
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86400000);
}

export function formatTime(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export function personName(team, id) {
  if (!id) return "";
  const person = (team || []).find((t) => t.id === id);
  return person ? person.name : id;
}

export function isMine(project, userId) {
  if (!userId) return false;
  return [...Object.keys(ROLE_LABEL), "next_action_owner"].some((f) => project[f] === userId);
}

export function filterRows(rows, view, userId) {
  if (view === "attention") return rows.filter((r) => (r.attention || []).length > 0);
  if (view === "mine") return rows.filter((r) => isMine(r, userId));
  return rows;
}

export function groupByCompany(rows) {
  const groups = [];
  const byId = {};
  rows.forEach((r) => {
    if (!byId[r.company_id]) {
      byId[r.company_id] = { company_id: r.company_id, company_name: r.company_name || r.company_id, rows: [] };
      groups.push(byId[r.company_id]);
    }
    byId[r.company_id].rows.push(r);
  });
  return groups;
}

export function HealthChip({ health }) {
  const h = HEALTH[health] || HEALTH.green;
  return (
    <Chip
      size="small"
      label={h.label}
      sx={{ backgroundColor: `${h.color}1A`, color: h.color, fontWeight: 600, border: `1px solid ${h.color}55` }}
    />
  );
}

export function StatusChip({ status }) {
  const muted = status === "closed" || status === "cancelled";
  return <Chip size="small" variant={muted ? "filled" : "outlined"} label={STATUS_LABEL[status] || status} />;
}

const AUTO_COLOR = "#2563EB";
const MANUAL_COLOR = "#334155";

// The SOP's 8 steps as segments: filled when done (blue when the platform
// recorded it), outlined for the current stage.
export function StageBar({ milestones, defs, stage, showLabel = true }) {
  const done = milestones || {};
  const current = (defs || []).find((m) => m.key === stage);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", gap: "3px" }} role="img" aria-label={`Stage: ${current ? current.stage || current.label : "Closed"}`}>
        {(defs || []).map((m) => {
          const step = done[m.key];
          const color = step ? (step.source === "auto" ? AUTO_COLOR : MANUAL_COLOR) : "transparent";
          return (
            <Tooltip key={m.key} title={`${m.label}${step ? " ✓" : ""}`}>
              <Box
                data-testid={`stage-${m.key}`}
                data-done={step ? "yes" : "no"}
                sx={{
                  flex: 1,
                  height: 8,
                  minWidth: 8,
                  borderRadius: 0.5,
                  backgroundColor: color,
                  border: `1.5px solid ${step ? color : m.key === stage ? "#D97706" : "#CBD5E1"}`,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      {showLabel && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {current ? current.stage || current.label : "Closed"}
        </Typography>
      )}
    </Box>
  );
}

// Renewal: amber within 60 days, red within 14 or past due.
export function renewalText(days) {
  if (days === null || days === undefined) return "";
  if (days < 0) return `${-days} day${days === -1 ? "" : "s"} past due`;
  if (days === 0) return "renews today";
  return `renews in ${days} day${days === 1 ? "" : "s"}`;
}

export function RenewalChip({ days }) {
  if (days === null || days === undefined) return null;
  const color = days <= 14 ? "error" : days <= 60 ? "warning" : "default";
  return <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={renewalText(days)} />;
}

// Epoch ms (stored at noon UTC) <-> the YYYY-MM-DD a date input holds.
export const msToDay = (ms) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
export const dayToMs = (day) => {
  if (!day) return null;
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y, m - 1, d, 12);
};
